import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import useApi from 'assets/hooks/api/useApi';
import { useDebounce } from 'assets/hooks/events/debounce/useDebounce';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { date, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import { compact, first, sum, values } from 'lodash';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import RefundConfigTemplate from 'models/productManagement/refundConfigTemplate/Model.RefundConfigTemplate';
import Contract from 'models/sales/contract/Model.Contract';
import ContractRefund from 'models/sales/contractRefund/Model.ContractRefund';
import contractRefundPricingInfoApi from 'models/sales/contractRefund/refundPricingInfo/Api.ContractRefund.RefundPricingInfo';
import contractRefundCommentDeleteApi from 'models/sales/contractRefundComment/delete/Api.ContractRefundComment.Delete';
import ContractRefundComment from 'models/sales/contractRefundComment/Model.ContractRefundComment';
import contractRefundCommentSaveApi from 'models/sales/contractRefundComment/save/Api.ContractRefundComment.Save';
import ContractRefundDealerPaymentControl from 'models/sales/contractRefundDealerPaymentControl/Model.ContractRefundDealerPaymentControl';
import ContractRefundStatusAuditLog from 'models/sales/contractRefundStatusAuditLog/Model.ContractRefundStatusAuditLog';
import Distributor from 'models/sales/distributor/Model.Distributor';
import Lender from 'models/sales/lender/Model.Lender';
import { useEffect, useMemo, useState } from 'react';
import { salesContractRefundIndexRoute } from './Sales.ContractRefund.Index';

export enum ContractRefundAction {
  INFO = 'info',
  DELETE = 'delete',
  PROCESS = 'process',
  DEALER_PAYMENT_CONTROLS = 'dealerPaymentControls',
  CANCEL = 'cancel',
}
export enum ContractRefundInfoTab {
  INFO = 'info',
  CONTRACT = 'contract',
  DOCUMENTS = 'documents',
  NOTES = 'notes',
  DEALER_PAYMENT_CONTROLS = 'dealerPaymentControls',
  STATUS_AUDIT_LOGS = 'statusAuditLogs',
}
export type VerifyContractRefundAction = Utils.VerifyExtends<
  Module.Sales.ContractRefund.Actions,
  Utils.ValueOf<typeof ContractRefundAction>
>;
export type ContractRefundForm = Omit<
  Api.Sales.ContractRefund.Save.IRequest,
  'contractRefundDocuments' | 'contractRefundComments'
> & {
  excludeDealerProfit: boolean;
  excludeASCDealerProfit: boolean;
  excludeAdministrationFee: boolean;
  contractRefundDocuments: Array<
    Api.Sales.ContractRefundDocument.Save.IRequest & {
      formFile?: Hooks.Input.File.Info;
    }
  >;
  contractRefundComments: Array<
    Api.Sales.ContractRefundComment.Save.IRequest & { tempCreatedAtUtcUnix: number; createdAtUtc: string; user: User }
  >;
};
export default function useContractRefund() {
  const { user, isDealer, isDistributor, permissions } = useAuthStore();
  const pageRouter = usePageRouter<Module.Sales.ContractRefund.Params, Module.Sales.ContractRefund.Query>({
    route: salesContractRefundIndexRoute,
  });
  const canView =
    permissions.VIEW_CONTRACTREFUND || permissions.CREATE_CONTRACTREFUND || permissions.EDIT_CONTRACTREFUND;

  const [isRecalculating, setIsRecalculating] = useState<boolean>(false);

  const listApi = useApi(
    {
      action: ContractRefund.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        dealerId: user?.dealers?.map((d) => d.id) ?? [],
        distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      },
      body: pageRouter.query.list,
      wait: !canView,
    },
    [JSON.stringify(pageRouter.query.list)]
  );
  const findApi = useApi(
    {
      action: ContractRefund.find,
      wait: !canView || !pageRouter.params.id,
      body: {
        id: Number(pageRouter.params.id),
      },
    },
    [pageRouter.params.id]
  );
  const selected = findApi?.payload;
  const contractCancellation = selected?.contractCancellation;
  const contractId = contractCancellation?.contractId;

  const contractListApi = useApi(
    {
      action: Contract.list,
      wait: !canView || !contractId,
      body: {
        id: [contractId],
        includeClaims: true,
      },
    },
    [contractId]
  );
  const contract = first(contractListApi?.payload?.data);
  const contractDealer = contract?.dealer;
  const vehicle = contract?.vehicle;
  const client = contract?.client;

  const lenderId = contract?.vehicle?.lenderId;
  const lenderFindApi = useApi(
    {
      action: Lender.find,
      wait: !canView || !lenderId,
      body: { id: lenderId },
    },
    [lenderId]
  );
  const lender = lenderId ? lenderFindApi?.payload : undefined;

  const dealerListApi = useApi({
    action: Dealer.list,
    wait: true,
    body: {
      id: user?.dealers?.map((d) => d.id) ?? undefined,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
    },
  });
  const distributorListApi = useApi({
    action: Distributor.list,
    wait: !isDealer && !isDistributor,
  });
  const userListApi = useApi({
    action: User.list,
    body: {
      isDisabled: false,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      dealerId: compact(user?.dealers?.map((it) => it.id)),
    },
    wait: !canView,
  });

  const refundConfigTemplateListApi = useApi(
    {
      action: RefundConfigTemplate.list,
      default: {
        isEnabled: true,
      },
      body: {
        productId: contract?.productId ? [contract.productId] : undefined,
      },
      wait: !canView || !contract?.productId,
    },
    [contract?.productId]
  );

  const commentsApi = useApi({
    action: ContractRefundComment.list,
    wait: true,
  });
  useEffect(() => {
    if (selected?.id) {
      commentsApi.execute({ contractRefundId: selected?.id });
    } else {
      commentsApi.reset(true);
    }
  }, [selected?.id]);

  const dealerPaymentControlsApi = useApi({
    action: ContractRefundDealerPaymentControl.list,
    wait: true,
  });
  useEffect(() => {
    if (selected?.id) {
      dealerPaymentControlsApi.execute({ contractRefundId: [selected?.id] });
    } else {
      dealerPaymentControlsApi.reset(true);
    }
  }, [selected?.id]);

  const statusAuditLogsApi = useApi({
    action: ContractRefundStatusAuditLog.list,
    wait: true,
  });
  useEffect(() => {
    if (selected?.id) {
      statusAuditLogsApi.execute({ contractRefundId: [selected?.id] });
    } else {
      statusAuditLogsApi.reset(true);
    }
  }, [selected?.id]);

  const originalTotals = useMemo(
    () => ({
      retainedPricingDistributions:
        sum(values(selected?.pricingDistributions ?? {})?.map((it) => it.retained?.amount ?? 0)) ?? 0,
      retainedDistributorDistributions:
        sum(values(selected?.distributorDistributions ?? {})?.map((it) => it.retained?.amount ?? 0)) ?? 0,
      retainedSubfees: sum((selected?.subfees ?? [])?.map((it) => it.retained?.amount ?? 0)) ?? 0,
      retainedEnhancements: selected?.enhancements?.map((enh) => ({
        id: enh.enhancementId,
        retainedPricingDistributions:
          sum(values(enh.pricingDistributions ?? {})?.map((it) => it.retained?.amount ?? 0)) ?? 0,
        retainedDistributorDistributions:
          sum(values(enh.distributorDistributions ?? {})?.map((it) => it.retained?.amount ?? 0)) ?? 0,
      })),
    }),
    [selected]
  );

  return {
    originalTotals,
    canView,
    pageRouter,
    listApi,
    findApi,
    commentsApi,
    dealerPaymentControlsApi,
    statusAuditLogsApi,
    selected,
    permissions,
    dealerListApi,
    distributorListApi,
    refundConfigTemplateListApi,
    userListApi,
    isRecalculating,
    setIsRecalculating,
    contract,
    client,
    vehicle,
    contractDealer,
    lender,
    contractCancellation,
    views: {
      [ContractRefundAction.PROCESS]:
        pageRouter.params.action === ContractRefundAction.PROCESS &&
        ((permissions.CREATE_CONTRACTREFUND && !selected) || (permissions.EDIT_CONTRACTREFUND && selected)),
    },
  };
}

export function useContractRefundProvider() {
  return useDataProvider<ReturnType<typeof useContractRefund>>();
}

export function useContractRefundComments() {
  const { lang } = getLocales();
  const { selected: contractRefund, permissions, commentsApi } = useContractRefund();
  const auth = useAuthStore();
  const [action, setAction] = useState<'edit' | 'delete'>();
  const reloadComments = () => {
    commentsApi.execute((b) => b);
    form.reload();
    setAction(null);
  };
  const form = useForm<Utils.FormData<Model.IContractRefundComment>>({
    default: {
      contractRefundId: contractRefund?.id,
      userId: auth.userId,
      comment: undefined,
    },
    validation: (data, errors) => {
      if (!data.comment) errors.comment = lang.required;
    },
    onSubmit: async (data) => {
      const res = await contractRefundCommentSaveApi({
        ...data,
        modifiedAtUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
      });
      if (res.status !== HttpStatus.ok) addLog({ error: lang.unknownError });
      else {
        addLog({ success: lang.saveSuccess });
        reloadComments();
      }
    },
  });
  useEffect(() => {
    if (contractRefund?.id) {
      form.update({ contractRefundId: contractRefund?.id });
    }
  }, [contractRefund?.id]);

  const deleteApi = useApi({
    action: contractRefundCommentDeleteApi,
    callback: reloadComments,
    wait: true,
  });
  return {
    form,
    deleteApi,
    isLoadingComments: commentsApi?.isExecuting,
    comments: commentsApi?.payload?.data ?? [],
    action,
    setAction,
    permissions,
    commentsApi,
  };
}

export function useContractRefundRecalculation(
  form: Hooks.Form.Export<ContractRefundForm>,
  debounceTimeInMs: number = 1000
) {
  const { contract, setIsRecalculating } = useContractRefundProvider();
  const debouncedOdometer = useDebounce(form.data?.contractCancellation?.odometerReading, debounceTimeInMs);

  useEffect(() => {
    if (
      !(form.data?.contractCancellation?.cancellationDateLocal || debouncedOdometer) ||
      !(form.data?.refundConfigTemplateId || form.data?.refundConfigTemplateJson) ||
      !form.isUpdated ||
      !contract?.id
    ) {
      return;
    }

    let isMounted = true;
    (async () => {
      setIsRecalculating(true);

      const res = await contractRefundPricingInfoApi(
        { contractId: contract.id },
        {
          refundConfigTemplateId: form.data?.refundConfigTemplateId,
          refundConfigTemplate: form.data?.refundConfigTemplateJson,
          contractCancellation: {
            ...form.data?.contractCancellation,
            odometerReading: debouncedOdometer,
          },
          excludeDealerProfit: form.data?.excludeDealerProfit,
          excludeASCDealerProfit: form.data?.excludeASCDealerProfit,
          excludeAdministrationFee: form.data?.excludeAdministrationFee,
          areClaimDeductionsApplied: form.data?.areClaimDeductionsApplied,
        }
      );

      if (isMounted && res.status === HttpStatus.ok && res.payload) {
        form.merge({ ...res.payload });
        setIsRecalculating(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [
    form.data?.contractCancellation?.cancellationDateLocal,
    debouncedOdometer,
    contract?.id,
    form.data?.excludeDealerProfit,
    form.data?.excludeASCDealerProfit,
    form.data?.excludeAdministrationFee,
    form.data?.areClaimDeductionsApplied,
  ]);

  return {
    debouncedOdometer,
  };
}
